// =============================================================================
// Pintravel — Inspirations API
// =============================================================================
// Server state for the public post: the post itself, comments, hearts, media
// uploads and reports. Public reads work without a token; `hearted` simply
// comes back false then.
// =============================================================================

import type {
  CreateFromTripPayload,
  CreateReportPayload,
  FeedPage,
  FeedParams,
  Inspiration,
  InspirationComment,
  InspirationMedia,
  InspirationMediaPresignResponse,
  PlaceCard,
  UpdateInspirationPayload,
} from '@itravel/shared';
import {
  type InfiniteData,
  type QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { api } from '@/lib/api/instance';

interface Data<T> {
  data: T;
}

export interface Page<T> {
  data: T[];
  meta: { page: number; limit: number; total: number; hasMore: boolean };
}

export const inspirationKeys = {
  all: ['inspirations'] as const,
  feed: (params: FeedParams) => [...inspirationKeys.all, 'feed', params] as const,
  one: (id: string) => [...inspirationKeys.all, 'one', id] as const,
  byAuthor: (authorId: string) => [...inspirationKeys.all, 'author', authorId] as const,
  byPlace: (placeId: string) => [...inspirationKeys.all, 'place', placeId] as const,
  placeCard: (placeId: string) => ['places', 'card', placeId] as const,
  mine: (status?: string) => [...inspirationKeys.all, 'mine', status ?? 'all'] as const,
  comments: (id: string) => [...inspirationKeys.all, 'comments', id] as const,
};

type ListShape = Page<Inspiration> | InfiniteData<Page<Inspiration>>;
type InspirationPatch = Partial<Inspiration> | ((item: Inspiration) => Partial<Inspiration>);

function apply(item: Inspiration, patch: InspirationPatch): Inspiration {
  return { ...item, ...(typeof patch === 'function' ? patch(item) : patch) };
}

function patchList(list: ListShape, id: string, patch: InspirationPatch): ListShape {
  const patchPage = (page: Page<Inspiration>) => ({
    ...page,
    data: page.data.map((item) => (item.id === id && 'media' in item ? apply(item, patch) : item)),
  });
  return 'pages' in list
    ? { ...list, pages: list.pages.map(patchPage) }
    : 'data' in list && Array.isArray(list.data)
      ? patchPage(list)
      : list;
}

/**
 * The same Inspiration sits in the feed, on profiles, on Place and Board pages
 * and on its own page. A heart or a save must show on all of them at once, not
 * only where it was pressed.
 */
export function patchInspirationEverywhere(
  queryClient: QueryClient,
  id: string,
  patch: InspirationPatch,
) {
  queryClient.setQueryData<Inspiration>(inspirationKeys.one(id), (current) =>
    current ? apply(current, patch) : current,
  );
  queryClient.setQueriesData<ListShape>({ queryKey: inspirationKeys.all }, (current) =>
    current && typeof current === 'object' ? patchList(current, id, patch) : current,
  );
}

// -----------------------------------------------------------------------------
// Reads
// -----------------------------------------------------------------------------

export async function fetchInspiration(id: string): Promise<Inspiration> {
  return (await api.get(`inspirations/${id}`).json<Data<Inspiration>>()).data;
}

export function useInspiration(id: string | undefined) {
  return useQuery({
    queryKey: inspirationKeys.one(id ?? ''),
    queryFn: () => fetchInspiration(id!),
    enabled: Boolean(id),
  });
}

async function fetchByAuthor(authorId: string, page: number): Promise<Page<Inspiration>> {
  return api
    .get('inspirations', { searchParams: { author: authorId, page: String(page), limit: '20' } })
    .json<Page<Inspiration>>();
}

export function useAuthorInspirations(authorId: string | undefined) {
  return useInfiniteQuery({
    queryKey: inspirationKeys.byAuthor(authorId ?? ''),
    queryFn: ({ pageParam }) => fetchByAuthor(authorId!, pageParam),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.meta.hasMore ? last.meta.page + 1 : undefined),
    enabled: Boolean(authorId),
  });
}

export function usePlaceCard(placeId: string | undefined) {
  return useQuery({
    queryKey: inspirationKeys.placeCard(placeId ?? ''),
    queryFn: async () => (await api.get(`places/records/${placeId}`).json<Data<PlaceCard>>()).data,
    enabled: Boolean(placeId),
  });
}

/** The caller's own note on a Place; an empty one removes it. */
export function useSetPlaceNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ placeId, note }: { placeId: string; note: string }) =>
      (
        await api
          .put(`places/records/${placeId}/note`, { json: { note } })
          .json<Data<{ note: string | null }>>()
      ).data,
    onSuccess: (_result, { placeId }) =>
      void queryClient.invalidateQueries({ queryKey: inspirationKeys.placeCard(placeId) }),
  });
}

/** The Inspirations pointing at a Place, best first. */
export function usePlaceInspirations(placeId: string | undefined) {
  return useInfiniteQuery({
    queryKey: inspirationKeys.byPlace(placeId ?? ''),
    queryFn: async ({ pageParam }) =>
      api
        .get(`inspirations/by-place/${placeId}`, {
          searchParams: { page: String(pageParam), limit: '20' },
        })
        .json<Page<Inspiration>>(),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.meta.hasMore ? last.meta.page + 1 : undefined),
    enabled: Boolean(placeId),
  });
}

export function useMyInspirations(status?: 'DRAFT' | 'PUBLISHED') {
  return useInfiniteQuery({
    queryKey: inspirationKeys.mine(status),
    queryFn: async ({ pageParam }) =>
      api
        .get('inspirations/mine', {
          searchParams: { page: String(pageParam), limit: '20', ...(status ? { status } : {}) },
        })
        .json<Page<Inspiration>>(),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.meta.hasMore ? last.meta.page + 1 : undefined),
  });
}

// -----------------------------------------------------------------------------
// The explore feed
// -----------------------------------------------------------------------------

/**
 * Pages of the feed. Ids already shown go back to the server as `seen` on
 * every next page, so a refresh that changes the seed does not repeat them.
 */
export function useFeed(params: FeedParams, enabled = true) {
  return useInfiniteQuery({
    queryKey: inspirationKeys.feed(params),
    queryFn: async ({ pageParam }) => {
      const searchParams: Record<string, string> = {
        page: String(pageParam.page),
        limit: '20',
        ...(params.lat !== undefined ? { lat: String(params.lat) } : {}),
        ...(params.lng !== undefined ? { lng: String(params.lng) } : {}),
        ...(params.radiusKm !== undefined ? { radiusKm: String(params.radiusKm) } : {}),
        ...(params.q ? { q: params.q } : {}),
        ...(params.seed ? { seed: params.seed } : {}),
        ...(params.scope ? { scope: params.scope } : {}),
        ...(pageParam.seen.length > 0 ? { seen: pageParam.seen.join(',') } : {}),
      };
      return api.get('inspirations/feed', { searchParams }).json<FeedPage>();
    },
    initialPageParam: { page: 1, seen: [] as string[] },
    getNextPageParam: (last, pages) =>
      last.meta.hasMore
        ? { page: last.meta.page + 1, seen: pages.flatMap((p) => p.data.map((d) => d.id)) }
        : undefined,
    enabled,
    // Coming back to the feed keeps the order shown; pull-to-refresh is what reshuffles.
    staleTime: Infinity,
  });
}

// -----------------------------------------------------------------------------
// Composer
// -----------------------------------------------------------------------------

export async function createDraft(): Promise<Inspiration> {
  return (await api.post('inspirations').json<Data<Inspiration>>()).data;
}

/** A draft from a trip's photos, already READY; the composer takes it from there. */
export async function createFromTrip(payload: CreateFromTripPayload): Promise<Inspiration> {
  return (await api.post('inspirations/from-trip', { json: payload }).json<Data<Inspiration>>())
    .data;
}

export async function updateInspiration(
  id: string,
  payload: UpdateInspirationPayload,
): Promise<Inspiration> {
  return (await api.patch(`inspirations/${id}`, { json: payload }).json<Data<Inspiration>>()).data;
}

export async function publishInspiration(id: string): Promise<Inspiration> {
  return (await api.post(`inspirations/${id}/publish`).json<Data<Inspiration>>()).data;
}

export async function deleteInspiration(id: string): Promise<void> {
  await api.delete(`inspirations/${id}`);
}

export async function presignMedia(
  id: string,
  mediaId: string,
  mimeType: string,
): Promise<InspirationMediaPresignResponse> {
  return (
    await api
      .post(`inspirations/${id}/media/presign`, { json: { mediaId, mimeType } })
      .json<Data<InspirationMediaPresignResponse>>()
  ).data;
}

export async function completeMedia(
  id: string,
  mediaId: string,
  details: {
    width: number;
    height: number;
    latitude?: number;
    longitude?: number;
    takenAt?: string;
  },
): Promise<InspirationMedia> {
  return (
    await api
      .post(`inspirations/${id}/media/${mediaId}/complete`, { json: details })
      .json<Data<InspirationMedia>>()
  ).data;
}

export async function deleteMedia(id: string, mediaId: string): Promise<void> {
  await api.delete(`inspirations/${id}/media/${mediaId}`);
}

export async function reorderMedia(id: string, orderedIds: string[]): Promise<Inspiration> {
  return (
    await api
      .patch(`inspirations/${id}/media/order`, { json: { orderedIds } })
      .json<Data<Inspiration>>()
  ).data;
}

/** Drops a draft (or a published post) and everything the caches know about it. */
export function useDeleteInspiration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteInspiration,
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: inspirationKeys.one(id) });
      void queryClient.invalidateQueries({ queryKey: inspirationKeys.all });
    },
  });
}

// -----------------------------------------------------------------------------
// Hiding — "not interested"
// -----------------------------------------------------------------------------

/** The feeds a hidden post leaves: Home's and a Place's. */
const HIDEABLE_LISTS = [
  [...inspirationKeys.all, 'feed'],
  [...inspirationKeys.all, 'place'],
] as const;

export function useHideInspiration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.post(`inspirations/${id}/hide`);
    },
    // Out of the lists already loaded, without a refetch reshuffling the feed.
    onSuccess: (_result, id) => {
      for (const queryKey of HIDEABLE_LISTS) {
        queryClient.setQueriesData<InfiniteData<Page<Inspiration>>>({ queryKey }, (current) =>
          current?.pages
            ? {
                ...current,
                pages: current.pages.map((page) => ({
                  ...page,
                  data: page.data.filter((item) => item.id !== id),
                })),
              }
            : current,
        );
      }
    },
  });
}

export function useUnhideInspiration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`inspirations/${id}/hide`);
    },
    onSuccess: () => {
      for (const queryKey of HIDEABLE_LISTS) void queryClient.invalidateQueries({ queryKey });
    },
  });
}

// -----------------------------------------------------------------------------
// Hearts — optimistic, the way a like should feel
// -----------------------------------------------------------------------------

export function useHeartInspiration(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (hearted: boolean) =>
      (
        await (
          hearted ? api.post(`inspirations/${id}/hearts`) : api.delete(`inspirations/${id}/hearts`)
        ).json<Data<{ heartCount: number; hearted: boolean }>>()
      ).data,
    onMutate: async (hearted) => {
      await queryClient.cancelQueries({ queryKey: inspirationKeys.one(id) });
      // Every cached copy is nudged by one: the tile pressed on the feed may
      // be the only copy there is, and it carries its own count.
      patchInspirationEverywhere(queryClient, id, (item) => ({
        hearted,
        heartCount: item.heartCount + (hearted ? 1 : -1),
      }));
    },
    onError: (_error, hearted) => {
      patchInspirationEverywhere(queryClient, id, (item) => ({
        hearted: !hearted,
        heartCount: item.heartCount + (hearted ? -1 : 1),
      }));
    },
    onSuccess: (result) => {
      patchInspirationEverywhere(queryClient, id, result);
      void queryClient.invalidateQueries({ queryKey: inspirationKeys.all, refetchType: 'none' });
    },
  });
}

// -----------------------------------------------------------------------------
// Comments
// -----------------------------------------------------------------------------

export function useInspirationComments(id: string | undefined) {
  return useInfiniteQuery({
    queryKey: inspirationKeys.comments(id ?? ''),
    queryFn: async ({ pageParam }) =>
      api
        .get(`inspirations/${id}/comments`, {
          searchParams: { page: String(pageParam), limit: '30' },
        })
        .json<Page<InspirationComment>>(),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.meta.hasMore ? last.meta.page + 1 : undefined),
    enabled: Boolean(id),
  });
}

export function useAddComment(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (content: string) =>
      (
        await api
          .post(`inspirations/${id}/comments`, { json: { content } })
          .json<Data<InspirationComment>>()
      ).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: inspirationKeys.comments(id) });
      void queryClient.invalidateQueries({ queryKey: inspirationKeys.one(id) });
    },
  });
}

export function useDeleteComment(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (commentId: string) => {
      await api.delete(`inspirations/${id}/comments/${commentId}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: inspirationKeys.comments(id) });
      void queryClient.invalidateQueries({ queryKey: inspirationKeys.one(id) });
    },
  });
}

export function useHeartComment(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ commentId, hearted }: { commentId: string; hearted: boolean }) =>
      (
        await (
          hearted
            ? api.post(`inspirations/${id}/comments/${commentId}/hearts`)
            : api.delete(`inspirations/${id}/comments/${commentId}/hearts`)
        ).json<Data<{ heartCount: number; hearted: boolean }>>()
      ).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: inspirationKeys.comments(id) });
    },
  });
}

// -----------------------------------------------------------------------------
// Reports
// -----------------------------------------------------------------------------

export function useReport() {
  return useMutation({
    mutationFn: async (payload: CreateReportPayload) =>
      (await api.post('reports', { json: payload }).json<Data<{ hidden: boolean }>>()).data,
  });
}
